import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";

const noop = () => {};

const defaultContextValue = {
  cartItems: [],
  cartCount: 0,
  cartSubtotal: 0,
  addToCart: noop,
  removeFromCart: noop,
  updateQuantity: noop,
  clearCart: noop,
  wishlistItems: [],
  wishlist: [],
  wishlistCount: 0,
  addWishlist: noop,
  removeWishlist: noop,
  toggleWishlist: noop,
  isWishlisted: () => false,
};

export const CartContext = createContext(defaultContextValue);

const catalogById = new Map(
  productData.map((product) => [String(product.id), product])
);

const getStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch (_error) {
    return null;
  }
};

const writeStorage = (key, value) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Storage can be unavailable in private browsing or when its quota is full.
  }
};

const readStoredArray = (key, normalize) => {
  const storage = getStorage();
  if (!storage) return [];

  try {
    const storedValue = storage.getItem(key);
    if (storedValue === null) return [];

    const parsedValue = JSON.parse(storedValue);
    if (!Array.isArray(parsedValue)) {
      writeStorage(key, []);
      return [];
    }

    const normalizedValue = normalize(parsedValue);
    if (JSON.stringify(normalizedValue) !== JSON.stringify(parsedValue)) {
      writeStorage(key, normalizedValue);
    }

    return normalizedValue;
  } catch (_error) {
    writeStorage(key, []);
    return [];
  }
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const resolveId = (productOrId) => {
  if (productOrId && typeof productOrId === "object") {
    return productOrId.id ?? productOrId.productId;
  }

  return productOrId;
};

const hasValidId = (id) => id !== null && id !== undefined && id !== "";

const idsMatch = (firstId, secondId) =>
  String(firstId) === String(secondId);

const normalizeQuantity = (quantity, fallback = 1) => {
  const numericQuantity = Number(quantity);
  if (!Number.isFinite(numericQuantity)) return fallback;
  return Math.max(1, Math.floor(numericQuantity));
};

export const getCartLineKey = (id, pageStyle = "", size = "") =>
  [id, pageStyle, size].map((value) => encodeURIComponent(normalizeText(value))).join("::");

const normalizeSelection = (selection = {}) => ({
  pageStyle: normalizeText(selection.pageStyle ?? selection.style),
  size: normalizeText(selection.size),
  color: normalizeText(selection.color),
});

const normalizeStoredCart = (items) =>
  items.reduce((normalizedItems, storedItem) => {
    if (!storedItem || typeof storedItem !== "object") return normalizedItems;

    const id = resolveId(storedItem);
    if (!hasValidId(id)) return normalizedItems;

    const selection = normalizeSelection(storedItem);
    const lineKey = getCartLineKey(id, selection.pageStyle, selection.size);
    const existingItem = normalizedItems.find(
      (item) => item.lineKey === lineKey
    );

    if (existingItem) {
      existingItem.quantity += normalizeQuantity(storedItem.quantity);
      if (selection.color) existingItem.color = selection.color;
      return normalizedItems;
    }

    normalizedItems.push({
      id,
      lineKey,
      pageStyle: selection.pageStyle,
      size: selection.size,
      color: selection.color,
      quantity: normalizeQuantity(storedItem.quantity),
    });

    return normalizedItems;
  }, []);

const normalizeStoredWishlist = (items) =>
  items.reduce((normalizedItems, storedItem) => {
    const id = resolveId(storedItem);
    if (!hasValidId(id)) return normalizedItems;

    if (!normalizedItems.some((savedId) => idsMatch(savedId, id))) {
      normalizedItems.push(id);
    }

    return normalizedItems;
  }, []);

const resolveAddArguments = (
  productOrId,
  quantityOrOptions,
  pageStyleOrOptions,
  size,
  color
) => {
  const productOptions =
    productOrId && typeof productOrId === "object" ? productOrId : {};
  let quantity = 1;
  let suppliedOptions = {};

  if (quantityOrOptions && typeof quantityOrOptions === "object") {
    suppliedOptions = quantityOrOptions;
    quantity = quantityOrOptions.quantity ?? productOptions.quantity ?? 1;
  } else {
    quantity = quantityOrOptions ?? productOptions.quantity ?? 1;
    suppliedOptions =
      pageStyleOrOptions && typeof pageStyleOrOptions === "object"
        ? pageStyleOrOptions
        : {
            pageStyle: pageStyleOrOptions,
            size,
            color,
          };
  }

  return {
    id: resolveId(productOrId),
    quantity: normalizeQuantity(quantity),
    selection: normalizeSelection({
      ...productOptions,
      ...suppliedOptions,
    }),
  };
};

const resolveLineArguments = (productOrId, pageStyleOrOptions, size) => {
  const productOptions =
    productOrId && typeof productOrId === "object" ? productOrId : {};
  const suppliedOptions =
    pageStyleOrOptions && typeof pageStyleOrOptions === "object"
      ? pageStyleOrOptions
      : { pageStyle: pageStyleOrOptions, size };

  return {
    id: resolveId(productOrId),
    selection: normalizeSelection({
      ...productOptions,
      ...suppliedOptions,
    }),
  };
};

const resolveUpdateArguments = (
  productOrId,
  pageStyleOrOptions,
  sizeOrQuantity,
  quantity
) => {
  if (pageStyleOrOptions && typeof pageStyleOrOptions === "object") {
    return {
      ...resolveLineArguments(productOrId, pageStyleOrOptions),
      quantity: sizeOrQuantity ?? pageStyleOrOptions.quantity,
    };
  }

  if (sizeOrQuantity && typeof sizeOrQuantity === "object") {
    return {
      ...resolveLineArguments(productOrId, sizeOrQuantity),
      quantity: pageStyleOrOptions,
    };
  }

  return {
    ...resolveLineArguments(productOrId, pageStyleOrOptions, sizeOrQuantity),
    quantity,
  };
};

const parsePrice = (price) => {
  if (typeof price === "number") return Number.isFinite(price) ? price : 0;
  if (typeof price !== "string") return 0;

  const parsedPrice = Number.parseFloat(price.replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(parsedPrice) ? parsedPrice : 0;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredArray(CART_STORAGE_KEY, normalizeStoredCart)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredArray(WISHLIST_STORAGE_KEY, normalizeStoredWishlist)
  );

  useEffect(() => {
    writeStorage(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    writeStorage(WISHLIST_STORAGE_KEY, wishlistItems);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("wishlist:updated"));
    }
  }, [wishlistItems]);

  const addToCart = useCallback(
    (
      productOrId,
      quantityOrOptions = 1,
      pageStyleOrOptions = "",
      size = "",
      color = ""
    ) => {
      const { id, quantity, selection } = resolveAddArguments(
        productOrId,
        quantityOrOptions,
        pageStyleOrOptions,
        size,
        color
      );

      if (!hasValidId(id)) return;

      const lineKey = getCartLineKey(id, selection.pageStyle, selection.size);

      setCartItems((currentItems) => {
        const matchingIndex = currentItems.findIndex(
          (item) => item.lineKey === lineKey
        );

        if (matchingIndex === -1) {
          return [
            ...currentItems,
            {
              id,
              lineKey,
              pageStyle: selection.pageStyle,
              size: selection.size,
              color: selection.color,
              quantity,
            },
          ];
        }

        return currentItems.map((item, index) =>
          index === matchingIndex
            ? {
                ...item,
                color: selection.color || item.color,
                quantity: item.quantity + quantity,
              }
            : item
        );
      });
    },
    []
  );

  const removeFromCart = useCallback(
    (productOrId, pageStyleOrOptions = "", size = "") => {
      const { id, selection } = resolveLineArguments(
        productOrId,
        pageStyleOrOptions,
        size
      );
      if (!hasValidId(id)) return;

      const lineKey = getCartLineKey(id, selection.pageStyle, selection.size);
      setCartItems((currentItems) =>
        currentItems.filter((item) => item.lineKey !== lineKey)
      );
    },
    []
  );

  const updateQuantity = useCallback(
    (
      productOrId,
      pageStyleOrOptions = "",
      sizeOrQuantity = "",
      quantity
    ) => {
      const resolvedArguments = resolveUpdateArguments(
        productOrId,
        pageStyleOrOptions,
        sizeOrQuantity,
        quantity
      );
      if (!hasValidId(resolvedArguments.id)) return;

      const numericQuantity = Number(resolvedArguments.quantity);
      if (!Number.isFinite(numericQuantity)) return;

      const lineKey = getCartLineKey(
        resolvedArguments.id,
        resolvedArguments.selection.pageStyle,
        resolvedArguments.selection.size
      );
      const nextQuantity = normalizeQuantity(numericQuantity);

      setCartItems((currentItems) =>
        currentItems.map((item) =>
          item.lineKey === lineKey
            ? { ...item, quantity: nextQuantity }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const addWishlist = useCallback((productOrId) => {
    const id = resolveId(productOrId);
    if (!hasValidId(id)) return;

    setWishlistItems((currentItems) =>
      currentItems.some((savedId) => idsMatch(savedId, id))
        ? currentItems
        : [...currentItems, id]
    );
  }, []);

  const removeWishlist = useCallback((productOrId) => {
    const id = resolveId(productOrId);
    if (!hasValidId(id)) return;

    setWishlistItems((currentItems) =>
      currentItems.filter((savedId) => !idsMatch(savedId, id))
    );
  }, []);

  const toggleWishlist = useCallback((productOrId) => {
    const id = resolveId(productOrId);
    if (!hasValidId(id)) return;

    setWishlistItems((currentItems) =>
      currentItems.some((savedId) => idsMatch(savedId, id))
        ? currentItems.filter((savedId) => !idsMatch(savedId, id))
        : [...currentItems, id]
    );
  }, []);

  const isWishlisted = useCallback(
    (productOrId) => {
      const id = resolveId(productOrId);
      return (
        hasValidId(id) &&
        wishlistItems.some((savedId) => idsMatch(savedId, id))
      );
    },
    [wishlistItems]
  );

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const cartSubtotal = useMemo(() => {
    const subtotal = cartItems.reduce((total, item) => {
      const product = catalogById.get(String(item.id));
      return total + parsePrice(product?.price) * item.quantity;
    }, 0);

    return Number(subtotal.toFixed(2));
  }, [cartItems]);

  const contextValue = useMemo(
    () => ({
      cartItems,
      cartCount,
      cartSubtotal,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      wishlistItems,
      wishlist: wishlistItems,
      wishlistCount: wishlistItems.length,
      addWishlist,
      removeWishlist,
      toggleWishlist,
      isWishlisted,
    }),
    [
      addToCart,
      addWishlist,
      cartCount,
      cartItems,
      cartSubtotal,
      clearCart,
      isWishlisted,
      removeFromCart,
      removeWishlist,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
